import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronsUp, ChevronsDown } from 'lucide-react'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { PriceRangeFormField } from 'src/shared/components/ui/forms/form-fields/price-range'
import { SelectFormField } from 'src/shared/components/ui/forms/form-fields/select'
import { TextareaFormField } from 'src/shared/components/ui/forms/form-fields/textarea'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { propertyTypeMap, propertyTypeSchema } from 'src/shared/schemas/referrals/referral-fields/shared/property-type'
import { timeframeMap, timeframeSchema } from 'src/shared/schemas/referrals/referral-fields/shared/timeframe'
import { useEventStore } from 'src/shared/store/event-store'

const propertyInspectorSchema = z.object({
  propertyType: propertyTypeSchema,
  budget: z.object({
    min: z.number().min(0),
    max: z.number().min(0),
  }),
  // Optional fields
  yearBuilt: z
    .number()
    .int('Year must be an integer')
    .positive('Year must be positive')
    .min(1800, 'Year must be after 1800')
    .max(new Date().getFullYear(), 'Year cannot be in the future')
    .optional(),
  squareFootageEstimate: z.number().max(120000).optional(),
  timeframe: timeframeSchema.optional(),
  knownPropertyIssues: z.string().max(1000).optional(),
  additionalTestNeeded: z.string().max(1000).optional(),
})

type PropertyInspectorFormData = z.infer<typeof propertyInspectorSchema>

export const PropertyInspectorInfo: React.FC = () => {
  const handleNext = useSendReferralStore((state) => state.handleNext)
  const handleBack = useSendReferralStore((state) => state.handleBack)
  const selectedReferral = useSendReferralStore((state) => state.selectedReferral)
  const referralFormData = useSendReferralStore((state) => state.referralFormData)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId
  const [showOptional, setShowOptional] = useState(false)

  const form = useForm<PropertyInspectorFormData>({
    resolver: zodResolver(propertyInspectorSchema),
    mode: 'onSubmit',
    defaultValues: {
      propertyType: referralFormData.propertyType || selectedReferral?.propertyType || undefined,
      budget: {
        min: referralFormData.budget?.min || selectedReferral?.budget?.min || 100000,
        max: referralFormData.budget?.max || selectedReferral?.budget?.max || 450000,
      },
      yearBuilt: referralFormData.yearBuilt || selectedReferral?.yearBuilt || undefined,
      knownPropertyIssues: referralFormData.knownPropertyIssues || selectedReferral?.knownPropertyIssues || undefined,
      additionalTestNeeded:
        referralFormData.additionalTestNeeded || selectedReferral?.additionalTestNeeded || undefined,
      squareFootageEstimate:
        referralFormData.squareFootageEstimate || selectedReferral?.squareFootageEstimate || undefined,
      timeframe: referralFormData.timeframe || selectedReferral?.timeframe || undefined,
    },
  })

  const onSubmit = async (data: PropertyInspectorFormData) => {
    if (currentUser) {
      sendEvent({
        userId: currentUser.id,
        eventType: 'flow_step',
        info: {
          sessionId,
          flowType: 'send_referral',
          flowStep: 'submit_property_inspector_info',
          flowId,
          ...data,
        },
      })
    }

    handleNext(data)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mb-24 space-y-8">
        <SelectFormField
          control={form.control}
          name="propertyType"
          label="Property Type"
          options={propertyTypeMap}
          required
        />

        <PriceRangeFormField control={form.control} name="budget" label="Budget" required />

        <Button
          variant="link"
          type="button"
          onClick={() => setShowOptional(!showOptional)}
          className="flex w-full items-center justify-center gap-2 text-muted hover:text-muted"
        >
          {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />} Optional Info{' '}
          {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />}
        </Button>

        {showOptional && (
          <>
            <div className="flex flex-row justify-between gap-3">
              <InputFormField
                control={form.control}
                name="yearBuilt"
                label="Year Built"
                placeholder="Enter..."
                floatingLabel={false}
                type="number"
                max={new Date().getFullYear()}
                fieldClassName="bg-accent"
              />

              <InputFormField
                control={form.control}
                name="squareFootageEstimate"
                label="Square Footage"
                placeholder="Enter..."
                floatingLabel={false}
                type="number"
                max={120000}
                fieldClassName="bg-accent"
              />
            </div>

            <SelectFormField control={form.control} name="timeframe" label="Timeline" options={timeframeMap} />

            <TextareaFormField
              control={form.control}
              name="knownPropertyIssues"
              label="Known Property Issues"
              description="e.g., 'Kitchen renovation needed', 'Roof repair'"
              maxLength={1000}
            />

            <TextareaFormField
              control={form.control}
              name="additionalTestNeeded"
              label="Additional Tests Needed"
              description="e.g., 'Eco-friendly materials', 'High-end finishes'"
              maxLength={1000}
            />
          </>
        )}

        <FixedFooter className="fixed inset-x-0">
          <div className="flex w-full gap-3">
            <Button variant="link" type="button" onClick={handleBack} className="w-full">
              Previous
            </Button>
            <Button type="submit" className="w-full">
              Next
            </Button>
          </div>
        </FixedFooter>
      </form>
    </Form>
  )
}
